import { api } from '@packages/backend/convex/_generated/api';
import { getUserErrorMessage, type ServiceHour } from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { format, parse } from 'date-fns';
import { useState } from 'react';
import { toast } from 'sonner';
import { ActionButton } from '@/components/ActionButton';
import { ActionDropdown } from '@/components/ActionDropdown';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useRestaurant } from '@/routes/restaurant/useRestaurantData';
import { ServiceHourForm, WEEK_DAYS } from './ServiceHourForm';

export const Route = createFileRoute('/r/$restaurantId/s/service-hours')({
  component: ServiceHoursPage,
});

function ServiceHoursPage() {
  const convex = useConvex();
  const { restaurantId } = Route.useParams();
  const restaurant = useRestaurant();
  const [formState, setFormState] = useState<ServiceHour | 'new' | 'closed'>('closed');

  const handleDelete = async (serviceHourUuid: string) => {
    try {
      await convex.mutation(api.restaurants.deleteServiceHour, {
        restaurantId,
        serviceHourUuid,
      });
      toast.success('Deleted successfully');
    } catch (error) {
      toast.error("Couldn't delete", {
        description: getUserErrorMessage(error),
      });
    }
  };

  return (
    <>
      {formState !== 'closed' && (
        <ServiceHourForm
          onClose={() => setFormState('closed')}
          editServiceHour={formState === 'new' ? undefined : formState}
        />
      )}
      <Card>
        <CardHeader>
          <CardTitle>Service Hours</CardTitle>
          <CardDescription>Online ordering hours</CardDescription>
          <CardAction>
            <ActionButton onClick={() => setFormState('new')}>Create New</ActionButton>
          </CardAction>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px]">Service</TableHead>
                <TableHead className="hidden md:table-cell">Hours</TableHead>
                <TableHead className="w-[64px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {restaurant.serviceHours.map((sh) => {
                const openTime = format(parse(sh.opens, "'T'HH:mm", new Date()), 'h:mm a');
                const closeTime = format(parse(sh.closes, "'T'HH:mm", new Date()), 'h:mm a');
                return (
                  <TableRow key={sh.uuid}>
                    <TableCell>
                      <Badge className="text-xs" variant="secondary">
                        {sh.serviceType}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="font-medium">{`${openTime} to ${closeTime}`}</div>
                      <div className="text-sm text-muted-foreground">
                        {WEEK_DAYS.filter((day) => sh.daysOfWeek.includes(day))
                          .map((day) => day.substring(0, 3))
                          .join(', ')}
                      </div>
                    </TableCell>
                    <TableCell>
                      <ActionDropdown
                        onDelete={() => handleDelete(sh.uuid)}
                        onEdit={() => setTimeout(() => setFormState(sh), 0)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </>
  );
}
